import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../../supabaseClient.jsx";
import { useCart } from "../../../shared/contexts/CartContext.jsx";
import { hasActiveSubscription } from "../subscription/subscriptionStatus.js";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value ?? 0);

function resolveDescricao(descricao) {
  if (!descricao) return "Pacote de horas para uso em agendamentos.";
  if (typeof descricao === "string") return descricao;
  if (typeof descricao === "object") {
    return descricao.ideal_para || descricao.texto || descricao.chamada || "Pacote de horas para uso em agendamentos.";
  }
  return "Pacote de horas para uso em agendamentos.";
}

export default function CheckoutPacotePage() {
  const { pacoteId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [pacote, setPacote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);

  useEffect(() => {
    async function checkSubscription() {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        navigate("/login", { replace: true });
        return;
      }

      const ativa = await hasActiveSubscription({
        userId: user.id,
        email: user.email,
      });

      setHasSubscription(ativa);
      setSubscriptionChecked(true);
    }

    checkSubscription();
  }, [navigate]);

  useEffect(() => {
    if (!subscriptionChecked || !hasSubscription) return;

    async function fetchPacote() {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("produtos")
        .select("*")
        .eq("tipo", "pacote_horas")
        .eq("ativo", true)
        .eq("id", pacoteId)
        .single();

      if (fetchError) {
        setError("Não foi possível carregar o pacote selecionado.");
        setPacote(null);
      } else {
        setPacote(data);
      }

      setLoading(false);
    }

    fetchPacote();
  }, [pacoteId, subscriptionChecked, hasSubscription]);

  if (subscriptionChecked && !hasSubscription) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Card className="p-6 border-primary/30 bg-primary/5">
          <p className="text-sm font-medium text-foreground">
            Para comprar pacotes de horas, sua familia precisa escolher uma assinatura ativa primeiro.
          </p>
          <Button
            className="mt-4"
            onClick={() =>
              navigate(`/escolher-assinatura?next=${encodeURIComponent(`/checkout/pacote/${pacoteId}`)}`)
            }
          >
            Escolher assinatura
          </Button>
        </Card>
      </div>
    );
  }

  const handleGoToPayment = () => {
    if (!pacote) return;

    const precoUnitario = pacote.valor || 0;
    const horas = pacote.horas || 0;
    const total = precoUnitario * horas;

    if (total <= 0) {
      setError("Este pacote não possui um valor válido. Contate o suporte.");
      return;
    }

    addItem({
      id: pacote.id,
      tipo: "pacote",
      nome: pacote.nome,
      // preco = total do pacote (valor × horas), quantidade=1
      preco: total,
      // também enviamos o campo esperado pelo backend
      valor: total,
      horas,
      descricao: resolveDescricao(pacote.descricao),
    });

    navigate("/pagamento");
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="h-5 w-36 rounded bg-muted animate-pulse mb-3" />
        <div className="h-8 w-64 rounded bg-muted animate-pulse mb-2" />
        <div className="h-4 w-80 rounded bg-muted animate-pulse mb-6" />
        <Card className="p-6 space-y-4">
          <div className="h-4 w-full rounded bg-muted animate-pulse" />
          <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
          <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
        </Card>
      </div>
    );
  }

  if (error || !pacote) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Card className="p-6 border-destructive/40 bg-destructive/5">
          <p className="text-sm font-medium text-destructive">
            {error || "Pacote não encontrado."}
          </p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </Card>
      </div>
    );
  }

  const total = (pacote.valor || 0) * (pacote.horas || 0);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Checkout de pacote
      </p>
      <h1 className="mb-2 text-2xl font-bold text-foreground">{pacote.nome}</h1>
      <p className="mb-6 text-sm text-muted-foreground">{resolveDescricao(pacote.descricao)}</p>

      <Card className="p-6">
        <div className="mb-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Quantidade de horas</span>
          <strong className="text-foreground">{pacote.horas || 0}h</strong>
        </div>
        <div className="mb-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Valor por hora</span>
          <strong className="text-foreground">{formatCurrency(pacote.valor || 0)}</strong>
        </div>
        <div className="flex items-center justify-between border-t border-border pt-4">
          <span className="text-sm font-semibold text-muted-foreground">Total</span>
          <strong className="text-xl text-foreground">{formatCurrency(total)}</strong>
        </div>
      </Card>

      <div className="mt-6 flex gap-3">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Voltar
        </Button>
        <Button onClick={handleGoToPayment}>
          Adicionar ao carrinho e continuar
        </Button>
      </div>
    </div>
  );
}
